import React, { useRef, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';

/**
 * Validates timeline duration string.
 * Returns { isValid, error }
 */
export const validateTimeline = (val) => {
  if (!val || typeof val !== 'string' || !val.trim()) {
    return { isValid: true, error: null };
  }
  const clean = val.replace(/hr/gi, '').trim().replace(/[^0-9:]/g, '');
  if (!clean) {
    return { isValid: true, error: null };
  }

  if (clean.includes(':')) {
    const [hStr, mStr] = clean.split(':');
    const h = parseInt(hStr || '0', 10);
    const m = mStr !== undefined && mStr !== '' ? parseInt(mStr, 10) : 0;

    if (isNaN(h) || isNaN(m)) {
      return { isValid: false, error: 'Add proper time line in hours' };
    }

    // Minutes validation: 00 to 59 only
    if (m >= 60) {
      return { isValid: false, error: 'Add proper time line in hours' };
    }

    // Maximum 8 hours constraint
    if (h > 8 || (h === 8 && m > 0)) {
      return { isValid: false, error: 'Max 8 hours (00:00 - 08:00 hr)' };
    }

    return { isValid: true, error: null };
  } else {
    const h = parseInt(clean, 10);
    if (isNaN(h)) {
      return { isValid: false, error: 'Add proper time line in hours' };
    }
    if (h > 8) {
      return { isValid: false, error: 'Max 8 hours (00:00 - 08:00 hr)' };
    }
    return { isValid: true, error: null };
  }
};

export const isOverEightHours = (val) => {
  const result = validateTimeline(val);
  return !result.isValid;
};

/**
 * Formats timeline duration to HH:mm string.
 */
export const clampAndFormatTimeline = (val) => {
  if (!val || typeof val !== 'string') return '';
  const clean = val.replace(/hr/gi, '').trim().replace(/[^0-9:]/g, '');
  if (!clean) return '';

  let h = 0;
  let m = 0;

  if (clean.includes(':')) {
    const parts = clean.split(':');
    h = parseInt(parts[0] || '0', 10);
    m = parseInt(parts[1] || '0', 10);
  } else {
    h = parseInt(clean, 10);
    m = 0;
  }

  if (isNaN(h)) h = 0;
  if (isNaN(m)) m = 0;

  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const TimelineInputField = ({
  value = '',
  onChange,
  onBlur,
  onFocus,
  label = 'Add time line',
  placeholder = '00:00',
  id = 'task_timeline',
  name = 'timeline',
  error = false,
  helperText = '',
  disabled = false,
  required = false,
  fullWidth = true,
  variant = 'outlined',
  InputLabelProps = {},
  ...otherProps
}) => {
  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const validation = validateTimeline(value);
  const showError = Boolean(error || !validation.isValid);
  const displayHelperText = !validation.isValid
    ? validation.error
    : (helperText || (showError ? '' : 'Max 8 hours (00:00 - 08:00 hr)'));

  const handleChange = (e) => {
    let input = e.target.value || '';
    const prevVal = value || '';
    const isDeleting = input.length < prevVal.length;

    // Convert dot or space to colon if user typed it as separator
    input = input.replace(/[.\s]/g, ':').replace(/:{2,}/g, ':');
    // Strip any "hr" if pasted, keep only digits and colon
    input = input.replace(/hr/gi, '').trim().replace(/[^0-9:]/g, '');

    // Max 5 chars for "HH:mm"
    if (input.length > 5) {
      input = input.slice(0, 5);
    }

    if (!isDeleting) {
      // Auto-format single digit 1-9 to "0X:" and advance to minutes
      if (/^[1-9]$/.test(input)) {
        input = `0${input}:`;
      } else if (/^0[0-9]$/.test(input)) {
        input = `${input}:`;
      } else if (/^\d{2}$/.test(input) && !input.endsWith(':')) {
        input = `${input}:`;
      }
    }

    if (onChange) {
      onChange(input);
    }
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    if (value && value.trim()) {
      const clean = value.replace(/hr/gi, '').trim().replace(/[^0-9:]/g, '');
      let formatted = clean;
      if (clean.includes(':')) {
        const [hStr, mStr] = clean.split(':');
        const h = parseInt(hStr || '0', 10);
        const m = parseInt(mStr || '0', 10);
        formatted = `${String(isNaN(h) ? 0 : h).padStart(2, '0')}:${String(isNaN(m) ? 0 : m).padStart(2, '0')}`;
      } else if (clean) {
        const h = parseInt(clean, 10);
        formatted = `${String(isNaN(h) ? 0 : h).padStart(2, '0')}:00`;
      }
      if (onChange) {
        onChange(formatted);
      }
    }
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleKeyDown = (e) => {
    // Arrow Up / Down keys to increment/decrement by 30 minutes
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const clean = (value || '').replace(/hr/gi, '').replace(/[^0-9:]/g, '');
      let currentMinutes = 0;
      if (clean.includes(':')) {
        const [h, m] = clean.split(':').map(Number);
        currentMinutes = (isNaN(h) ? 0 : h) * 60 + (isNaN(m) ? 0 : m);
      } else if (clean) {
        const h = parseInt(clean, 10);
        currentMinutes = (isNaN(h) ? 0 : h) * 60;
      }

      const step = 30; // 30 minutes
      let nextMinutes = e.key === 'ArrowUp' ? currentMinutes + step : currentMinutes - step;
      if (nextMinutes < 0) nextMinutes = 0;
      if (nextMinutes > 8 * 60) nextMinutes = 8 * 60; // Max 8 hours

      const nh = Math.floor(nextMinutes / 60);
      const nm = nextMinutes % 60;
      const formatted = `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
      if (onChange) {
        onChange(formatted);
      }
    }
  };

  return (
    <div
      style={{ width: '100%', cursor: 'text' }}
      onClick={() => inputRef.current && inputRef.current.focus()}
    >
      <TextField
        fullWidth={fullWidth}
        variant={variant}
        id={id}
        name={name}
        label={label}
        autoComplete="off"
        placeholder={placeholder || '00:00'}
        value={value || ''}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        error={showError}
        helperText={displayHelperText}
        FormHelperTextProps={{
          style: {
            color: showError ? '#d32f2f' : '#64748b',
            fontWeight: showError ? 500 : 400,
            fontSize: '0.75rem',
            marginLeft: 0,
            marginTop: 4,
          },
        }}
        disabled={disabled}
        required={required}
        inputRef={inputRef}
        InputLabelProps={{
          shrink: true,
          ...InputLabelProps,
        }}
        InputProps={{
          autoComplete: 'off',
          style: {
            backgroundColor: 'transparent',
            background: 'transparent',
          },
          endAdornment: (
            <InputAdornment
              position="end"
              style={{
                marginLeft: 6,
                marginRight: 'auto',
                pointerEvents: 'none',
              }}
            >
              <span
                style={{
                  color: showError ? '#d32f2f' : '#64748b',
                  fontWeight: 400, // Explicitly normal, not bold
                  fontSize: 14,
                  userSelect: 'none',
                  letterSpacing: '0.2px',
                }}
              >
                hr
              </span>
            </InputAdornment>
          ),
        }}
        inputProps={{
          maxLength: 5,
          autoComplete: 'off',
          style: {
            boxSizing: 'content-box',
            width: '5.2ch',
            flex: 'none',
            paddingRight: 0,
            fontWeight: 500,
            letterSpacing: '0.5px',
            backgroundColor: 'transparent',
            background: 'transparent',
          },
        }}
        {...otherProps}
      />
    </div>
  );
};

export default TimelineInputField;
